import { styled } from "goober";
import type { ReactNode } from "react";
import { Box } from "../../Box";
import { Checkbox } from "../../Checkbox";
import { PressButton } from "../../PressButton";
import { Text } from "../../Text";
import { CopyableField } from "./CopyableField";
import { Field } from "./shared";
import { scimBaseUrl } from "./urls";

export type SyncMode = "off" | "jit" | "jit_users_and_groups" | "scim";

export type SyncModeState = {
  syncMode: SyncMode;
};

const SYNC_MODE_OPTIONS: {
  value: SyncMode;
  title: string;
  description: string;
}[] = [
  {
    value: "off",
    title: "Off",
    description:
      "Users are invited and group memberships are managed manually in the app",
  },
  {
    value: "jit",
    title: "JIT — users only",
    description:
      "Accounts are created on first SSO login; group memberships are managed manually in the app",
  },
  {
    value: "jit_users_and_groups",
    title: "JIT — users and groups",
    description:
      "Accounts are created on first SSO login, and group memberships sync on each login from the IdP’s “groups” claim",
  },
  {
    value: "scim",
    title: "SCIM",
    description:
      "IdP provisions users and groups via SCIM 2.0 in real time; users must be provisioned before they can sign in",
  },
];

const OptionsContainer = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

const OptionRow = styled(PressButton)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  width: 100%;
  text-align: left;
  user-select: none;
  &:not(:last-child) {
    border-bottom: 0.5px solid var(--border-alpha);
  }
  &:hover {
    background: var(--alpha4);
  }
`;

const OptionCheck = styled("div")`
  margin-left: auto;
  align-self: center;
  display: flex;
  &[data-selected="false"] {
    opacity: 0.2;
  }
`;

const GuardrailList = styled("ul")`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--text-secondary);
  & li::marker {
    color: var(--text-tertiary);
  }
`;

export function SyncModeFields({
  state,
  onChange,
  scimTokenSection,
}: {
  state: SyncModeState;
  onChange: (next: SyncModeState) => void;
  scimTokenSection?: ReactNode;
}) {
  return (
    <Field>
      <Box horizontal={8} vertical={6}>
        <Text variant="text-small-md">Sync mode</Text>
      </Box>
      <OptionsContainer>
        {SYNC_MODE_OPTIONS.map((option) => (
          <OptionRow
            key={option.value}
            data-no-pill
            onPress={() => onChange({ ...state, syncMode: option.value })}
          >
            <div>
              <Text variant="text-small-md">{option.title}</Text>
              <Box top={2}>
                <Text variant="text-small" color="var(--text-secondary)">
                  {option.description}
                </Text>
              </Box>
            </div>
            <OptionCheck data-selected={state.syncMode === option.value}>
              <Checkbox checked={state.syncMode === option.value} />
            </OptionCheck>
          </OptionRow>
        ))}
      </OptionsContainer>
      {state.syncMode === "scim" && (
        <Box top={16}>
          <Box horizontal={8} vertical={6}>
            <Text variant="text-small-md">How sync applies changes</Text>
          </Box>
          <Box horizontal={8}>
            <GuardrailList>
              <li>
                <Text variant="text-small" color="var(--text-secondary)">
                  Provisioning a group whose name already exists takes it over:
                  its members are replaced from your IdP, while its existing
                  access assignments are kept.
                </Text>
              </li>
              <li>
                <Text variant="text-small" color="var(--text-secondary)">
                  A group can’t be deleted while it still grants access to
                  spaces, apps, or other resources — remove those assignments
                  first.
                </Text>
              </li>
            </GuardrailList>
          </Box>
        </Box>
      )}
      {state.syncMode === "scim" && (
        <Box top={8}>
          <CopyableField
            label="SCIM 2.0 base URL"
            hint="Enter this as the SCIM endpoint (tenant URL) in your IdP"
            value={scimBaseUrl()}
          />
        </Box>
      )}
      {scimTokenSection}
    </Field>
  );
}
